import { watch, type WatchStopHandle } from 'vue'
import { getActivePinia, type Pinia } from 'pinia'
import { OfflineBootstrapService } from './bootstrap-service'
import {
  OFFLINE_SCHEMA_VERSION,
  PRICING_CONTRACT_VERSION,
  type OfflineCommandStatus,
  type OfflinePosContext,
} from './contracts'
import { db, type BigSaleDB } from './db'
import { buildOfflineContextKey, getOrCreateDeviceId } from './device-id'
import { OfflineSyncQueue } from './sync-queue'
import type { OfflineSyncBootstrap } from './sync-service'
import { normalizeDeviceTimezone } from './timezone'
import { isPosOfflineEnabledForBranch } from '@/shared/config/env'
import { useAuthStore, type RestoreSessionOptions } from '@/stores/auth.store'
import { useOfflineModeStore } from '@/stores/offline-mode.store'
import { useUserStore } from '@/stores/user.store'
import type { AuthSession } from '@/shared/types/auth.type'

export interface OfflinePosRuntimeOptions {
  database?: BigSaleDB
  isEnabledForBranch?: (branchId: number) => boolean
  getDeviceId?: () => string
  bootstrap?: OfflineSyncBootstrap
  persistStorage?: () => Promise<unknown>
  onError?: (error: unknown) => void
}

export interface OfflinePosRuntime {
  stop(): void
  whenIdle(): Promise<void>
}

export type SplashPhase = 'verifying' | 'bootstrap' | 'settings' | 'ready'

export interface OfflineAuthRestoreOptions {
  database?: BigSaleDB
  isEnabledForBranch?: (branchId: number) => boolean
  getDeviceId?: () => string
  onPhase?: (phase: SplashPhase) => void
}

const activeRuntimes = new WeakMap<Pinia, OfflinePosRuntime>()

async function hasUsableOfflineContext(
  session: AuthSession,
  database: BigSaleDB,
  enabledForBranch: (branchId: number) => boolean,
  deviceIdProvider: () => string,
): Promise<boolean> {
  const tenantCode = session.tenant.code.trim().toLowerCase()
  const branchId = session.currentBranchId

  if (
    !Number.isInteger(branchId) ||
    branchId === null ||
    branchId <= 0 ||
    !enabledForBranch(branchId)
  ) {
    return Promise.resolve(false)
  }

  const deviceId = deviceIdProvider()
  const contextKey = buildOfflineContextKey(tenantCode, branchId, deviceId)

  return database.transaction(
    'rw',
    [database.offlineContexts, database.salesTables, database.catalog],
    async () => {
      const context = await database.offlineContexts.get(contextKey)
      const payload = context?.signed_pricing_payload
      const isValid = Boolean(
        context &&
        context.context_key === contextKey &&
        context.tenant_code === tenantCode &&
        context.branch_id === branchId &&
        context.device_id === deviceId &&
        context.actor_user_id === session.user.id &&
        hasSamePermissions(context.permission_codes, session.permissions ?? []) &&
        ['online', 'offline', 'syncing'].includes(context.mode) &&
        context.schema_version === OFFLINE_SCHEMA_VERSION &&
        context.pricing_contract_version === PRICING_CONTRACT_VERSION &&
        context.bootstrap_id &&
        context.bootstrap_at &&
        context.pricing_signature &&
        payload &&
        payload.bundle_id === context.bootstrap_id &&
        payload.tenant_code === tenantCode &&
        payload.branch_id === branchId &&
        payload.schema_version === OFFLINE_SCHEMA_VERSION &&
        payload.pricing_contract_version === PRICING_CONTRACT_VERSION,
      )

      if (!isValid || !context) return false

      const tableCount = await database.salesTables.where('context_key').equals(contextKey).count()
      const catalogCount = await database.catalog
        .where('context_key')
        .equals(contextKey)
        .filter((item) => item.sellable)
        .count()
      if (tableCount === 0 || catalogCount === 0) return false

      if (context.mode !== 'offline') {
        const updated = await database.offlineContexts.update(contextKey, { mode: 'offline' })
        if (updated !== 1) return false
      }

      return true
    },
  )
}

export async function restoreAppAuthSession(
  pinia?: Pinia,
  options: OfflineAuthRestoreOptions = {},
): Promise<boolean> {
  const database = options.database ?? db
  const enabledForBranch = options.isEnabledForBranch ?? isPosOfflineEnabledForBranch
  const deviceIdProvider = options.getDeviceId ?? getOrCreateDeviceId
  const activePinia = pinia ?? getActivePinia() ?? undefined
  const authStore = useAuthStore(activePinia)
  const reportPhase = options.onPhase ?? (() => undefined)
  const restoreOptions: RestoreSessionOptions = {
    allowUnavailable: (session) =>
      hasUsableOfflineContext(session, database, enabledForBranch, deviceIdProvider),
  }

  reportPhase('verifying')

  const isAuthenticated = await authStore.restoreSessionFromApi(restoreOptions)

  if (!isAuthenticated) {
    // There is no authenticated tenant context to attach to the public settings
    // request. Skip it entirely on a first visit or after an invalid session.
    reportPhase('ready')
    return false
  }

  if (activePinia) {
    reportPhase('bootstrap')
    await activeRuntimes.get(activePinia)?.whenIdle()

    // Settings are tenant-scoped by the API despite the public endpoint name,
    // so fetch them only after the stored session has been verified (and, when
    // applicable, its access token has been refreshed).
    reportPhase('settings')
    await import('@/stores/system-setting.store')
      .then(({ useSystemSettingStore }) => useSystemSettingStore(activePinia).fetchPublic())
      .catch(() => undefined)
  }

  reportPhase('ready')

  return isAuthenticated
}

function browserTimezone(): string {
  return normalizeDeviceTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
}

function hasSamePermissions(left: string[], right: string[]): boolean {
  const actual = new Set(left)
  const expected = new Set(right)

  return (
    actual.size === expected.size && [...actual].every((permission) => expected.has(permission))
  )
}

function requestPersistentStorage(): Promise<unknown> {
  return navigator.storage?.persist?.() ?? Promise.resolve(false)
}

export function startOfflinePosRuntime(
  pinia: Pinia,
  options: OfflinePosRuntimeOptions = {},
): OfflinePosRuntime {
  const database = options.database ?? db
  const enabledForBranch = options.isEnabledForBranch ?? isPosOfflineEnabledForBranch
  const deviceIdProvider = options.getDeviceId ?? getOrCreateDeviceId
  const bootstrap = options.bootstrap ?? new OfflineBootstrapService(database)
  const persistStorage = options.persistStorage ?? requestPersistentStorage
  const reportError = options.onError ?? (() => undefined)
  const authStore = useAuthStore(pinia)
  const userStore = useUserStore(pinia)
  const modeStore = useOfflineModeStore(pinia)
  const queue = new OfflineSyncQueue(database)
  let generation = 0
  let stopped = false
  let work = Promise.resolve()

  async function reconcile(expectedGeneration: number): Promise<void> {
    if (stopped || expectedGeneration !== generation) return

    if (authStore.isAuthenticated && !authStore.isSessionVerified) return

    const branchId = authStore.currentBranchId
    const tenantCode = authStore.tenant?.code.trim().toLowerCase() ?? ''
    const actorUserId = authStore.user?.id ?? 0

    if (
      !authStore.isAuthenticated ||
      !Number.isInteger(branchId) ||
      branchId === null ||
      branchId <= 0 ||
      tenantCode === '' ||
      actorUserId <= 0 ||
      !enabledForBranch(branchId)
    ) {
      if (!stopped && expectedGeneration === generation) modeStore.deactivateContext()
      return
    }

    const deviceId = deviceIdProvider()
    const contextKey = buildOfflineContextKey(tenantCode, branchId, deviceId)
    const initialContext: OfflinePosContext = {
      context_key: contextKey,
      tenant_code: tenantCode,
      branch_id: branchId,
      device_id: deviceId,
      actor_user_id: actorUserId,
      permission_codes: [...userStore.permissions],
      branch_timezone: browserTimezone(),
      mode: 'online',
      schema_version: OFFLINE_SCHEMA_VERSION,
      pricing_contract_version: PRICING_CONTRACT_VERSION,
      bootstrap_id: null,
      bootstrap_at: null,
      server_time_offset_ms: 0,
      signed_pricing_payload: null,
      pricing_signature: null,
    }

    let storedContext = await database.offlineContexts.get(contextKey)

    if (storedContext && storedContext.actor_user_id !== actorUserId) {
      const unresolvedStatuses: OfflineCommandStatus[] = ['pending', 'syncing', 'failed', 'blocked']
      const unresolvedCount = await database.outboxCommands
        .where('[context_key+status]')
        .anyOf(unresolvedStatuses.map((status) => [contextKey, status]))
        .count()

      if (unresolvedCount > 0) {
        modeStore.blockContextActivation('OFFLINE_CONTEXT_ACTOR_MISMATCH')
        return
      }

      await database.offlineContexts.put(initialContext)
      storedContext = initialContext
    }

    if (
      storedContext &&
      storedContext.actor_user_id === actorUserId &&
      !hasSamePermissions(storedContext.permission_codes, initialContext.permission_codes)
    ) {
      await database.offlineContexts.update(contextKey, {
        permission_codes: [...initialContext.permission_codes],
      })
    }

    await modeStore.initializeContext(initialContext, database)
    if (stopped || expectedGeneration !== generation) return

    await queue.recoverInterruptedSync(contextKey)
    await modeStore.refreshPendingCount()
    if (stopped || expectedGeneration !== generation) return

    if (!modeStore.isOnlineMode || !modeStore.isConnected || modeStore.pendingCount > 0) return

    try {
      const bundle = await bootstrap.download()
      if (stopped || expectedGeneration !== generation) return
      await bootstrap.activate(contextKey, bundle)
      await modeStore.refreshBootstrapMetadata()
      modeStore.setLastBootstrapError(null)
      void persistStorage().catch(() => false)
    } catch (error) {
      const code =
        error instanceof Error && error.message.trim() !== ''
          ? error.message
          : 'OFFLINE_BOOTSTRAP_FAILED'
      modeStore.setLastBootstrapError(code)
      reportError(error)
    }
  }

  function schedule() {
    const expectedGeneration = ++generation
    work = work.then(() => reconcile(expectedGeneration)).catch(reportError)
  }

  const stopWatching: WatchStopHandle = watch(
    () => [
      authStore.isAuthenticated,
      authStore.isSessionVerified,
      authStore.tenant?.code,
      authStore.user?.id,
      authStore.currentBranchId,
      userStore.permissions.join('\u0000'),
      modeStore.isConnected,
    ],
    schedule,
    { immediate: true, flush: 'sync' },
  )

  const runtime: OfflinePosRuntime = {
    stop() {
      stopped = true
      generation += 1
      stopWatching()
      modeStore.deactivateContext()
      if (activeRuntimes.get(pinia) === runtime) activeRuntimes.delete(pinia)
    },
    async whenIdle() {
      let pending = work
      await pending
      while (pending !== work) {
        pending = work
        await pending
      }
    },
  }

  activeRuntimes.set(pinia, runtime)

  return runtime
}
